/* eslint-disable */
import productProcessMapServices from '@/Services/Analytics Services/product-process-map.services';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Building2, PackageSearch, ShoppingCart, Warehouse } from 'lucide-react';
import GlobalSearch from '@/Components/globalSearch/search.js';
import { useState, useCallback, useEffect, useMemo, } from 'react';
import useProductMappingStore from './productMappingStore';
import { SelectPicker } from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import VariantTable from '../Food Products/VariantPrice/VariantTable';
import { Store } from '@mui/icons-material';
import { getHeadersandRows } from './productMappigUtils';
import { Button } from '@/Components/ui/button';
import { CellData } from './CellData';
import { everyWordTitleCase, toTitleCase, useProductData } from '@/Helpers';
import { useAuth } from '@/Components/Auth/Login/auth';
import issued from '@/Images/issued.svg';
import produced from '@/Images/produced.svg';
import primaryPackaged from '@/Images/primaryPack.svg';
import secondaryPackaged from '@/Images/secondaryPack.svg';
import dispatched from '@/Images/externalDispatch.svg';
import wastage from '@/Images/wastage.svg';
import { modalActions } from '@/Actions';
import ProductSelectionDrillDownModal from '@/Components/Modals/ProductSelectionDrillDownModal/ProductSelectionDrillDownModal';

export const parentTabs = [
	{
		id: 1,
		label: 'Organization',
		value: 'ORGANIZATION',
		icon: <Building2 size={16} strokeWidth={1} />
	},
	{
		id: 2,
		label: 'Inventory',
		value: 'INVENTORY',
		icon: <Warehouse size={16} strokeWidth={1} />,
	},
	{
		id: 3,
		label: 'Sourcing',
		value: 'SOURCING',
		icon: <ShoppingCart size={16} strokeWidth={1} />,
	},
	{
		id: 4,
		label: 'Issuance',
		value: 'ISSUANCE',
		img: issued,
	},
	{
		id: 5,
		label: 'Production',
		value: 'PRODUCTION',
		img: produced,
	},
	{
		id: 6,
		label: 'Primary Packaging',
		value: 'PRIMARY_PACKAGING',
		img: primaryPackaged,
	},
	{
		id: 7,
		label: 'Secondary Packaging',
		value: 'SECONDARY_PACKAGING',
		img: secondaryPackaged,
	},
	{
		id: 8,
		label: 'Distribution',
		value: 'DISTRIBUTION',
		img: dispatched,
	},
	{
		id: 9,
		label: 'Wastage',
		value: 'WASTAGE',
		img: wastage,
	},
];

const SCROLLBAR_STYLE = `
  .custom-scrollbar::-webkit-scrollbar { height: 4px; }
  .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .custom-scrollbar::-webkit-scrollbar-thumb { background: #ffb703; border-radius: 10px; }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #e6a602; }
`;

// Helper functions outside component to prevent re-creation
const getSubTabs = (data, category) => {
	return data?.data?.mappingTypes
		?.filter((item) => item.category?.toLowerCase() === category?.toLowerCase())
		?.map((item) => ({
			...item,
			value: item.id,
			label: everyWordTitleCase(item.displayName),
		})) || []?.sort((a, b) => parseFloat(a.priority) - parseFloat(b.priority));
};

const buildDropDownList = (subTab, storeList, storeTypes) => {
	const list = {};
	if (subTab?.storeBased) {
		list['storeBased'] = storeList?.map((item) => ({
			...item,
			label: item?.name,
			value: item?.id,
			type: item?.type || 'Other', // Required for grouping
		})) || [];
	}
	if (subTab?.storeTypeBased) {
		list['storeTypeBased'] = storeTypes;
	}
	return list;
};

const getDefaultDropDownValues = (dropdowns) => {
	const defaults = {};
	if (dropdowns.storeBased?.length > 0) {
		defaults['storeBased'] = dropdowns.storeBased[0].value;
	}
	if (dropdowns.storeTypeBased?.length > 0) {
		defaults['storeTypeBased'] = dropdowns.storeTypeBased[0].value;
	}
	return defaults;
};

function ProductMapping() {
	const { data, isLoading: dataLoading } = useQuery({
		queryKey: ['productMapping'],
		queryFn: productProcessMapServices.fetchAvailableMappingTypes,
		staleTime: Infinity,
	});
	const { userConfig = {} } = useAuth();
	const { level } = userConfig?.userDetails || {};
	const view = level === 1;
	const storeList = useSelector((state) => state?.form?.STORES?.data?.LIST);
	const productList = useSelector((state) => state?.form?.ALL_PRODUCTS?.data?.LIST);
	const dispatch = useDispatch();
	const {
		parentTabList, childTabList, selectedParentTab, selectedChildTab,
		dropDownLists, selectedDropDownValue, productWiseMappingData,
		mappingDataValues, tableData,
		setSelectedDropDownValue, setProductWiseMappingData,
		setMappingDataValues, setTableData,
		updateTabs, setInitialData
	} = useProductMappingStore();

	const [searchTerm, setSearchTerm] = useState('');

	const { mutate: getMappingTypeValues, isLoading: getMappingTypeValuesLoading } = useMutation({
		mutationFn: (payload) => productProcessMapServices.getMappingTypeValues(payload),
		onSuccess: (response) => {
			if (response?.statusCode === 200) {
				setMappingDataValues(response?.data);
			}
		}
	});

	const { mutate: fetchProductWiseMappingData } = useMutation({
		mutationFn: (payload) => productProcessMapServices?.fetchProductWiseMappingData(payload),
		onSuccess: (response) => {
			if (response?.statusCode === 200) {
				setProductWiseMappingData(response?.data?.productwiseMappings);
			}
		}
	});

	const STORE_TYPES = useMemo(() => {
		return storeList?.reduce((acc, curr) => {
			const type = curr?.type;
			const subType = curr?.subType;
			const label = `${type}${subType ? ` (${subType})` : ''}`;
			const value = `${type}${subType ? `__${subType}` : ''}`;
			if (!acc.find((item) => item.value === value)) {
				acc.push({ value, label });
			}
			return acc;
		}, []);
	}, [storeList]);

	const fetchMappingData = useCallback(({ subTab, storeId = null, storeType = null }) => {
		getMappingTypeValues({
			data: {
				mappingType: { id: subTab?.id },
				mappingOn: 'PRODUCT',
				store: storeId ? { id: storeId } : null,
				storeTypeSubType: storeType,
			},
		});
		fetchProductWiseMappingData({
			data: {
				store: { id: storeId },
				mappingTypes: subTab?.id ? [subTab?.id] : null,
			},
		});
	}, [getMappingTypeValues, fetchProductWiseMappingData]);

	// Initialize Tabs
	useEffect(() => {
		if (data?.statusCode === 200 && storeList) {
			const processedParentTabs = parentTabs.map(tab => ({
				...tab,
				disabled: getSubTabs(data, tab.value).length === 0
			}));

			const firstAvailableParent = processedParentTabs.find(tab => !tab.disabled) || processedParentTabs[0];
			const firstParent = firstAvailableParent?.value;
			const subTabs = getSubTabs(data, firstParent);
			const firstSubTab = subTabs?.[0];
			const dropdowns = buildDropDownList(firstSubTab, storeList, STORE_TYPES);
			const defaults = getDefaultDropDownValues(dropdowns);

			setInitialData({
				parentTabList: processedParentTabs,
				selectedParentTab: firstParent,
				childTabList: subTabs,
				selectedChildTab: firstSubTab,
				dropDownLists: dropdowns,
				selectedDropDownValue: defaults,
				tableData: { headers: [], rows: [] }
			});

			if (firstSubTab) {
				fetchMappingData({
					subTab: firstSubTab,
					storeId: defaults.storeBased,
					storeType: defaults.storeTypeBased
				});
			}
		}
	}, [data, storeList, STORE_TYPES, fetchMappingData, setInitialData]);

	// Update Table data when mappings change
	useEffect(() => {
		if ((mappingDataValues?.mappingOnValues || mappingDataValues?.mappingTypeValues) && productList?.length > 0) {
			const { headers, rows } = getHeadersandRows(mappingDataValues, productWiseMappingData, productList, selectedChildTab);
			setTableData({ headers, rows });
		}
	}, [mappingDataValues, productWiseMappingData, productList, setTableData]);

	const handleParentTab = (value) => {
		const tabConfig = parentTabList.find(t => t.value === value);
		if (tabConfig?.disabled) return;

		const subTabs = getSubTabs(data, value);
		const firstSubTab = subTabs?.[0];
		const dropdowns = buildDropDownList(firstSubTab, storeList, STORE_TYPES);
		const defaults = getDefaultDropDownValues(dropdowns);

		updateTabs(value, subTabs, firstSubTab, dropdowns, defaults);
		if (firstSubTab) {
			fetchMappingData({
				subTab: firstSubTab,
				storeId: defaults.storeBased,
				storeType: defaults.storeTypeBased
			});
		}
	};

	const handleChildTab = (value) => {
		const dropdowns = buildDropDownList(value, storeList, STORE_TYPES);
		const defaults = getDefaultDropDownValues(dropdowns);

		updateTabs(selectedParentTab, childTabList, value, dropdowns, defaults);
		if (value) {
			fetchMappingData({
				subTab: value,
				storeId: defaults.storeBased,
				storeType: defaults.storeTypeBased
			});
		}
	};

	const handleDropDownValue = (key, value) => {
		const storeId = key === 'storeBased' ? value : selectedDropDownValue?.storeBased;
		const storeType = key === 'storeTypeBased' ? value : selectedDropDownValue?.storeTypeBased;
		setSelectedDropDownValue({ ...selectedDropDownValue, [key]: value });
		if (!value) return setTableData({ headers: [], rows: [] });
		fetchMappingData({ subTab: selectedChildTab, storeId, storeType });
	};

	console.log(tableData, "tableData")

	const customRowData = useMemo(() => {
		const base = {};
		tableData?.headers?.forEach((column) => {
			if (column?.key === 'product') {
				base[column.key] = (product) => (
					<div className='text-[#1A1A1A] text-[13px] font-bold leading-tight'>
						{product?.productName}
					</div>
				);
			} else {
				base[column.key] = (product, index, colKey, absoluteIndex) => {
					const isMapped = colKey === 'consolidated_binary'
						? !!product?.is_yes_mapped
						: product?.mappingIds?.some(id => String(id) === String(colKey));

					return (
						<CellData
							viewMode={true}
							key={`${product.id}-${colKey}`}
							initiallyMapped={isMapped}
							product={product}
							colKey={colKey}
							index={index}
							absoluteIndex={absoluteIndex}
							columnId={column?.id}
							mappingValueId={product?.mappingId}
							isYes={column?.isYes}
							isNo={column?.isNo}
						/>
					);
				};
			}
		});
		return base;
	}, [tableData?.headers]);

	const customCol = useCallback((column) => (
		<div className='text-[#8E6E2F] text-center font-bold text-[11px] uppercase tracking-wider'>
			{column?.title}
		</div>
	)
		, [tableData?.headers]);

	const handleFetchProductList = useCallback(() => {
		useProductData(dispatch);
	}, [dispatch]);

	const getCustomInfoLine = () => {
		if ((productList?.length === 0 && Array.isArray(productList)) || !productList) return (
			<div className='flex flex-col items-center justify-center gap-4 py-10 px-6 text-center bg-gray-50/50 rounded-2xl border border-dashed border-gray-200'>
				<div className='p-4 bg-white rounded-full shadow-sm ring-1 ring-black/5'>
					<PackageSearch size={40} className='text-gray-400' />
				</div>
				<div className='flex flex-col gap-1'>
					<h3 className='text-lg font-bold text-gray-900'>Products not loaded successfully</h3>
					<p className='text-sm text-gray-500 max-w-[250px]'>
						We couldn't find any products. Please fetch products to continue with mapping.
					</p>
				</div>
				<Button
					className='mt-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-6 py-2 font-bold shadow-md hover:shadow-lg transition-all rounded-lg'
					onClick={handleFetchProductList}
				>
					Fetch Products
				</Button>
			</div>
		);
		if (selectedChildTab?.storeBased && !selectedDropDownValue?.storeBased) return (
			<div className='flex flex-col items-center justify-center gap-4 py-12 px-6 text-center animate-in fade-in zoom-in duration-300'>
				<div className='p-4 bg-yellow-50 rounded-full shadow-inner ring-1 ring-yellow-100'>
					<Store sx={{ fontSize: 40, color: '#eab308' }} />
				</div>
				<div className='flex flex-col gap-1'>
					<h3 className='text-lg font-bold text-gray-900'>Select a store to proceed</h3>
					<p className='text-sm text-gray-500 max-w-[250px] mx-auto'>
						Please choose a store from the dropdown to view and manage its product mappings.
					</p>
				</div>
			</div>
		);
		if (selectedChildTab?.storeTypeBased && !selectedDropDownValue?.storeTypeBased) return (
			<div className='flex flex-col items-center justify-center gap-4 py-12 px-6 text-center animate-in fade-in zoom-in duration-300'>
				<div className='p-4 bg-yellow-50 rounded-full shadow-inner ring-1 ring-yellow-100'>
					<Store sx={{ fontSize: 40, color: '#eab308' }} />
				</div>
				<div className='flex flex-col gap-1'>
					<h3 className='text-lg font-bold text-gray-900'>Select a store type to proceed</h3>
					<p className='text-sm text-gray-500 max-w-[250px] mx-auto'>
						Please choose a store type from the dropdown to view the associated product mappings.
					</p>
				</div>
			</div>
		);
		return 'No mapping data found for the selected filters';
	}

	const sortValueInfo = useMemo(() => {
		const sort = {};
		tableData?.headers?.forEach((ele) => {
			if (ele.key === 'product') {
				sort[ele.key] = {
					customValue: true,
					type: 'string',
					value: (obj) => obj?.productName || ''
				};
			} else {
				sort[ele.key] = {
					customValue: true,
					type: 'number',
					value: (obj) => {
						const isMapped = ele.key === 'consolidated_binary'
							? !!obj?.is_yes_mapped
							: obj?.mappingIds?.some(id => String(id) === String(ele.key));
						return isMapped ? 1 : 0;
					}
				}
			}
		})
		return sort;
	}, [tableData?.headers]);

	const handleOpenModal = () => {
		dispatch(modalActions.toggleProductSelectionDrillDownModal(true));
	}

	const filteredRows = useMemo(() => {
		if (!searchTerm) return tableData?.rows || [];
		return tableData?.rows?.filter((row) =>
			row?.productName?.toLowerCase()?.includes(searchTerm?.toLowerCase())
		);
	}, [tableData?.rows, searchTerm]);

	return (
		<div className='p-4'>
			<ProductSelectionDrillDownModal refreshData={() => fetchMappingData({ subTab: selectedChildTab, storeId: selectedDropDownValue?.storeBased, storeType: selectedDropDownValue?.storeTypeBased })} />
			<style>{SCROLLBAR_STYLE}</style>
			<div className='flex justify-between items-center mb-4'>
				<h1 className='text-xl font-semibold'> Digital Factory - Configurations</h1>
				<div className='flex items-center gap-4 text-[12px]'>
					<div className='flex items-center gap-1.5'>
						<div className='w-4 h-4 rounded bg-green-50 flex items-center justify-center border border-green-200'>
							<div className='w-2 h-2 rounded-full bg-green-500' />
						</div>
						<span className='text-slate-600 font-medium'>Mapped</span>
					</div>
					<div className='flex items-center gap-1.5'>
						<div className='w-4 h-4 rounded bg-red-50 flex items-center justify-center border border-red-200'>
							<div className='w-2 h-2 rounded-full bg-red-500' />
						</div>
						<span className='text-slate-600 font-medium'>Not Mapped</span>
					</div>
				</div>
			</div>
			<div className='flex flex-col gap-3'>
				{/* Parent Tabs */}
				<div className='flex w-fit justify-center rounded-md bg-[#FDE6AD] border border-[#00000033]'>
					{parentTabList?.map((ele) => (
						<div
							key={ele.value}
							onClick={() => handleParentTab(ele.value)}
							className={`flex items-center gap-2 justify-center px-3 py-2 text-[11.5px] font-medium cursor-pointer whitespace-nowrap
                ${selectedParentTab === ele.value ? 'bg-[#ffb703]' : ''} 
                ${ele?.disabled ? 'opacity-50 cursor-not-allowed contrast-50 grayscale-[0.5]' : ''}`}
						>
							{ele?.img ?
								<img src={ele.img} alt={ele.label} className='w-4 h-4' /> : ele?.icon}
							{ele.label}
						</div>
					))}
				</div>
				<div className='flex gap-2 items-end w-full'>
					<div
						className={`flex flex-wrap gap-1 justify-start rounded-md bg-[#FDE6AD] border border-[#00000033] h-fit ${childTabList?.length > 8 ? 'w-full' : 'w-fit'}`}
					>
						{childTabList?.map((ele) => (
							<div
								key={ele.value}
								onClick={() => handleChildTab(ele)}
								className={`flex items-center gap-2 justify-center px-3 py-2 text-[11.5px] font-medium cursor-pointer whitespace-nowrap rounded-sm
                  ${selectedChildTab?.value === ele.value ? 'bg-[#ffb703]' : ''}`}
							>
								{ele.label}
							</div>

						))}
					</div>
				</div>

				<div className="flex flex-wrap items-center gap-4 w-full bg-white p-1 rounded-xl border border-gray-100 shadow-sm">
					<div className="flex-1 min-w-[250px]">
						<GlobalSearch
							searchDropDown={false}
							placeholderText="Search Products..."
							setSearchValue={setSearchTerm}
							width="100%"
						/>
					</div>

					{Object.entries(dropDownLists)?.map(([key, list]) => {
						const labelMap = {
							storeBased: 'Select Store *',
							storeTypeBased: 'Select Store Type *',
						};
						return (
							<div
								key={key}
								className="flex-1 min-w-[200px]"
							>
								<SelectPicker
									data={list}
									value={selectedDropDownValue?.[key] || null}
									onChange={(value) => handleDropDownValue(key, value)}
									placeholder={labelMap[key] || toTitleCase(key)}
									className="w-full"
									menuClassName="z-[9999]"
									cleanable={false}
									groupBy={key === 'storeBased' ? 'type' : null}
								/>
							</div>
						);
					})}

					<Button
						className="w-fit bg-[#2563eb] text-[12px] hover:bg-[#1d4ed8] text-white px-6 py-2.5 font-bold shadow-md hover:shadow-lg transition-all rounded-lg flex items-center gap-2"
						onClick={handleOpenModal}
					>
						<Store sx={{ fontSize: 18 }} />
						Update Mapping Config
					</Button>
				</div>

				<div className='h-[calc(100vh-300px)] overflow-y-auto rounded-xl shadow-sm border border-gray-100 bg-white'>
					<VariantTable
						data={filteredRows}
						columns={tableData?.headers}
						customCol={customCol}
						customRowData={customRowData}
						isLoading={getMappingTypeValuesLoading || dataLoading}
						customInfoLine={getCustomInfoLine()}
						virtualization={true}
						sortValueInfo={sortValueInfo}
					/>
				</div>
			</div>
		</div>
	);
}
export default ProductMapping;